import React, { useState, useEffect, useCallback } from 'react';
import { Calculator, RotateCcw, Scale, Sparkles } from 'lucide-react';
import { defaultCalculationSettings, mergeWithDefaultCalculationSettings } from '../utils/defaultSettings';

const cloneDefaults = () => JSON.parse(JSON.stringify(defaultCalculationSettings));

export const CalculationSettingsPage = () => {
  const [settings, setSettings] = useState(defaultCalculationSettings);

  useEffect(() => {
    const raw = localStorage.getItem('calculationSettings');
    if (raw) {
      try {
        const merged = mergeWithDefaultCalculationSettings(JSON.parse(raw));
        setSettings(merged);
        localStorage.setItem('calculationSettings', JSON.stringify(merged));
      } catch {
        const fresh = cloneDefaults();
        setSettings(fresh);
        localStorage.setItem('calculationSettings', JSON.stringify(fresh));
      }
    } else {
      const fresh = cloneDefaults();
      localStorage.setItem('calculationSettings', JSON.stringify(fresh));
      setSettings(fresh);
    }
  }, []);

  const persist = useCallback((next) => {
    setSettings(next);
    localStorage.setItem('calculationSettings', JSON.stringify(next));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const num = parseFloat(value);
    const updatedSettings = { ...settings, [name]: Number.isFinite(num) ? num : 0 };
    persist(updatedSettings);
  };

  const handleResetDefaults = () => {
    if (
      !window.confirm(
        'Tüm hesaplama ve dağıtım ayarları fabrika varsayılanlarına sıfırlanacak ve tarayıcıya kaydedilecek. Onaylıyor musunuz?'
      )
    ) {
      return;
    }
    const fresh = cloneDefaults();
    persist(fresh);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-slate-50 via-white to-indigo-50/30">
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <header className="mb-10 flex flex-col gap-6 border-b border-slate-200/80 pb-10 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50/80 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-indigo-700">
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              Tevzi uygulaması
            </div>
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Hesaplama ve dağıtım</h1>
                <p className="mt-3 text-base leading-relaxed text-slate-600">
              Dosya puanlarının nasıl hesaplanacağını ve dağıtım kurallarını buradan yönetebilirsiniz.
              Değişiklikler kaydedildiğinde tarayıcınızda saklanır.
            </p>
          </div>
          <button
            type="button"
            onClick={handleResetDefaults}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-amber-200 hover:bg-amber-50/80 hover:text-amber-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <RotateCcw className="h-4 w-4 text-amber-600" aria-hidden />
            Varsayılan ayarlara dön
          </button>
        </header>

        <div className="grid gap-8 lg:grid-cols-2">
          <section className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-lg shadow-slate-200/50 ring-1 ring-slate-100">
            <div className="flex items-center gap-4 border-b border-slate-100 bg-gradient-to-r from-indigo-50/90 to-white px-6 py-5">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-md shadow-indigo-600/25">
                <Calculator className="h-6 w-6" aria-hidden />
              </span>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Hesaplama ayarları</h2>
                <p className="text-sm text-slate-500">Sanık, mağdur ve dosya bileşen puanları</p>
              </div>
            </div>
            <div className="space-y-1 p-6 sm:p-8">
              <SettingInput label="Sanık puanı" name="sanikPuani" value={settings.sanikPuani} onChange={handleChange} />
              <SettingInput label="Mağdur puanı" name="magdurPuani" value={settings.magdurPuani} onChange={handleChange} />
              <SettingInput label="Normal dosya puanı" name="dosyaPuani" value={settings.dosyaPuani} onChange={handleChange} />
              <SettingInput
                label="Kalın dosya (KD) puanı"
                name="kalinDosyaPuani"
                value={settings.kalinDosyaPuani}
                onChange={handleChange}
              />
              <SettingInput label="Klasör (K) puanı" name="klasorPuani" value={settings.klasorPuani} onChange={handleChange} />
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-lg shadow-slate-200/50 ring-1 ring-slate-100">
            <div className="flex items-center gap-4 border-b border-slate-100 bg-gradient-to-r from-violet-50/90 to-white px-6 py-5">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-600 text-white shadow-md shadow-violet-600/25">
                <Scale className="h-6 w-6" aria-hidden />
              </span>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Dağıtım ayarları</h2>
                <p className="text-sm text-slate-500">Hakim çarpanı ve yeni hakim limitleri</p>
              </div>
            </div>
            <div className="space-y-1 p-6 sm:p-8">
              <SettingInput
                label="Hakim kendi dosyası çarpanı"
                name="hakimKendiDosyasiCarpani"
                value={settings.hakimKendiDosyasiCarpani}
                onChange={handleChange}
                step="0.05"
              />

              <div className="pt-4">
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">Yeni hakim limitleri</h3>
                <SettingInput
                  label="Maksimum sanık sayısı"
                  name="maxSanikSayisi"
                  value={settings.maxSanikSayisi}
                  onChange={handleChange}
                />
                <SettingInput
                  label="Maksimum mağdur sayısı"
                  name="maxMagdurSayisi"
                  value={settings.maxMagdurSayisi}
                  onChange={handleChange}
                />
                <SettingInput label="Maksimum puan" name="maxPuan" value={settings.maxPuan} onChange={handleChange} />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

const SettingInput = ({ label, name, value, onChange, step = '1' }) => (
  <div className="group py-2">
    <label htmlFor={name} className="mb-1.5 block text-sm font-medium text-slate-700">
      {label}
    </label>
    <input
      id={name}
      type="number"
      name={name}
      step={step}
      value={value}
      onChange={onChange}
      className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2.5 text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
    />
  </div>
);
